<script setup>
import {
  computed,
  onMounted,
  ref,
  toRefs,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import ImageComponent from '@/components/common/ImageComponent.vue';
import { apiClient } from '@/apis';
import PreviewHeader from '@/components/preview/kakao/common/PreviewHeader.vue';
import PreviewFooter from '@/components/preview/kakao/common/PreviewFooter.vue';
import { COUPON_TYPE_OPTIONS } from '@/config/constant';

const { t } = useI18n();

const props = defineProps({
  position: {
    type: String,
    default: '',
  },
  friendtalkPremiumVideo: {
    type: Object,
    default: () => ({}),
  },
  kakaoProfile: {
    type: String,
    default: '',
  },
});

const videoInfo = ref({});
const thumbnailElm = ref(null);
const videoElm = ref(null);
// 너비 / 높이
const videoRatio = ref(0.5);
const imageWidth = ref(0);

const {
  friendtalkPremiumVideo,
  position,
} = toRefs(props);

const imageUrl = computed(() => friendtalkPremiumVideo.value.imageUrl);

const videoTitle = computed(() => {
  const { header } = friendtalkPremiumVideo.value;

  // return header || t('kakaoBasic.preview.title');
  return header || '';
});

const videoContent = computed(() => {
  const { content = '' } = friendtalkPremiumVideo.value;

  if (!content) {
    // return [t('kakaoBasic.preview.content')];
    return [];
  }

  return content.split('\n').map((v) => v || '\u00A0');
});

// const imageClass = computed(() => {
//   const hover = position.value === 'premium-video-image';

//   return hover ? 'editAreaPositionHover' : '';
// });

const headerClass = computed(() => {
  const hover = position.value === 'premium-video-header';

  return hover ? 'editAreaPositionHover' : '';
});

const contentClass = computed(() => {
  if (!videoContent.value.length) {
    return 'd-none';
  }

  const hover = position.value === 'premium-video-content';

  return hover ? 'editAreaPositionHover' : '';
});

const buttonsClass = computed(() => {
  const hover = position.value === 'premium-video-buttons';

  return (hover && friendtalkPremiumVideo.value.buttons?.length) ? 'editAreaPositionHover' : '';
});

const couponClass = computed(() => {
  const list = [];
  const hover = position.value === 'premium-video-coupon';

  if (hover) {
    list.push('editAreaPositionHover');
  }

  if (!friendtalkPremiumVideo.value.coupon?.enabled) {
    list.push('d-none');
  }

  return list.join(' ');
});

const videoClass = computed(() => {
  const hover = position.value === 'premium-video-video';

  return hover ? 'editAreaPositionHover' : '';
});

const videoId = computed(() => {
  const { videoUrl = '' } = friendtalkPremiumVideo.value;

  if (videoUrl.startsWith('https://tv.kakao.com/v/')) {
    return videoUrl.slice(23);
  }

  if (/^https:\/\/tv\.kakao\.com\/channel\/\d+(\/cliplink\/\d+)?$/.test(videoUrl)) {
    return videoUrl.match(/\/cliplink\/(\d+)/)?.[1];
  }

  return '';
});

const setVideoRatio = (videoOutputList) => {
  videoOutputList.some(({ width, height }) => {
    if (width === 0 || height === 0) {
      return false;
    }

    const ratio = height / width;

    if (ratio > 1.3) {
      videoRatio.value = 1.3;
      return true;
    }

    videoRatio.value = height / width;
    return true;
  });
};

const setVideo = async (value) => {
  if (!value) {
    videoRatio.value = 0.5;
    return;
  }

  if (videoInfo.value[value]) {
    setVideoRatio(videoInfo.value[value]);
    return;
  }

  const response = await apiClient.get(`/kakao/friendtalk/external/premium-video-info/${value}`);

  const videoOutputList = response?.clipLink?.clip?.videoOutputList;

  if (!videoOutputList?.length) {
    videoInfo.value[value] = true;
    return;
  }

  setVideoRatio(videoOutputList);

  videoInfo.value[value] = videoOutputList;
};

watch(videoId, setVideo);
setVideo(videoId.value);

const thumbnailImageDisplay = computed(() => {
  if (friendtalkPremiumVideo.value.imageUrl) {
    return true;
  }

  if (!videoId.value) {
    return true;
  }

  if (!videoInfo.value[videoId.value]) {
    return true;
  }

  return false;
});

const styleString = computed(() => {
  if (!imageWidth.value) {
    return '';
  }

  return `height: ${parseInt(imageWidth.value * videoRatio.value, 10)}px;`;
});

onMounted(() => {
  imageWidth.value = parseFloat(window.getComputedStyle(thumbnailElm.value.$el).width);
});

const couponType = COUPON_TYPE_OPTIONS.reduce((current, item) => ({
  [`${item.type}`]: item.text,
  ...current,
}), {});

const couponTitle = computed(() => {
  const title = friendtalkPremiumVideo.value.coupon?.name;
  const type = friendtalkPremiumVideo.value.coupon?.type;
  const str = t(couponType[type] ?? 'empty');
  const value = type === 'DELIVERY_DISCOUNT' ? str : `${title}${str.slice(2)}`;

  return value;
});

const couponDescription = computed(() => friendtalkPremiumVideo.value.coupon?.description || '');

defineOptions({
  inheritAttrs: false,
});

</script>
<template>
  <div class="preview-talk preview-talk--active">
    <div class="preview-inner mockup-scroll">
      <PreviewHeader
        :kakao-profile="kakaoProfile"
      />
      <div class="preview-message preview-message--active commerce">
        <div class="preview-message__container">
          <div class="preview-message__content">
            <div
              class="preview-message__inner-img preview-message__inner--video"
              :class="videoClass"
            >
              <ImageComponent
                ref="thumbnailElm"
                :src="imageUrl"
                alt="commerce-image"
                :className="thumbnailImageDisplay ? '' : 'd-none'"
                :styleString="styleString"
              />
              <iframe
                ref="videoElm"
                v-if="!thumbnailImageDisplay"
                :src="`https://play-tv.kakao.com/embed/player/cliplink/${videoId}`"
                frameborder="0"
                scrolling="no"
                :style="styleString"
              ></iframe>
              <div class="video__icon"></div>
            </div>

            <div class="preview-message__inner">
              <div
                class="video__title"
                :class="headerClass"
              >
                {{ videoTitle }}
              </div>
              <div
                class="video__desc"
                :class="contentClass"
              >
                <div
                  v-for="(text, i) in videoContent"
                  :key="`videoContent-${i}`"
                >
                  {{ text }}
                </div>
              </div>
            </div>
            <div
              class="preview-message__button"
              :class="buttonsClass"
            >
              <button
                v-for="(item, index) in friendtalkPremiumVideo.buttons"
                :key="`kakao-preview-button${index}`"
                :class="{ 'preview-message__button--yellow': item.type === 'BF' }"
                type="button"
              >
                {{ item.name }}
              </button>
            </div>
            <div class="preview-message__coupon">
              <button
                type="button"
                :class="couponClass"
              >
                <div class="coupon__text">
                  <h5 class="coupon__title">{{ couponTitle }}</h5>
                  <p class="coupon__desc">{{ couponDescription }}</p>
                </div>
                <div class="coupon__icon">
                  <i></i>
                  <span>COUPON</span>
                </div>
              </button>
            </div>
          </div>
        </div>
        <PreviewFooter />
      </div>
    </div>
  </div>
</template>
<style scoped>
</style>
